import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Banner from '@/components-latest/primitives/Banner'
import Button from '@/components-latest/primitives/Button'
import RadioGroup from '@/components-latest/primitives/Controls/RadioGroup'
import { InfoCircle } from '@/components-latest/primitives/Icons'
import Spinner from '@/components-latest/primitives/Spinner'
import Textarea from '@/components-latest/primitives/Textarea'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import { isErrorType } from '@/config/http/http.helpers'
import { useFetchRejectionReasonsQuery } from '@/ducks/orders/services/orders.api'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'
import { useServerTranslation } from '@/hooks/useServerTranslation/useServerTranslation'
import { pushGtmFormSubmitEvent } from '@/utils/gtm.utils'

import Styles from './CancelOrderModal.module.scss'

interface FormData {
  reasonType: string
  comment: ''
}

interface ICancelOrderModalProps {
  // `isOtherReason`: the store picked "Other", so `reason` is its free text.
  onConfirm: (reason: string, isOtherReason: boolean) => Promise<unknown> | void
  children: React.ReactNode
}

const CancelOrderModal: React.FC<ICancelOrderModalProps> = ({
  onConfirm,
  children
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    register,
    unregister
  } = useForm<FormData>({
    defaultValues: {
      reasonType: '',
      comment: ''
    },
    mode: 'onSubmit',
    criteriaMode: 'all'
  })

  const storeId = useSelector(selectStoreId) || ''
  const { isFetching, data } = useFetchRejectionReasonsQuery(
    { store_id: storeId },
    { skip: !storeId }
  )

  const choosenReason = watch('reasonType')

  const otherReason = t('orders.cancel-reasons.other')

  useEffect(() => {
    if (choosenReason === otherReason) {
      register('comment', { required: 'Comment is required' })
    } else {
      unregister('comment')
    }
  }, [choosenReason, register, unregister, otherReason])

  const { getTranslation } = useServerTranslation()

  const onSubmit = async (formData: FormData) => {
    pushGtmFormSubmitEvent()
    const { reasonType, comment } = formData
    const reason = reasonType === otherReason && comment ? comment : reasonType

    setIsSubmitting(true)
    setSubmitError(null)
    try {
      // The rejection only counts once the server has acknowledged it: closing
      // the modal before that made a dropped POST look like a success.
      await onConfirm(reason, reasonType === otherReason)
      setOpen(false)
    } catch (error) {
      setSubmitError(
        (isErrorType(error) && error.message) || t('common.error-state.title')
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOpenChange = (state: boolean) => {
    if (isSubmitting) return
    if (state) setSubmitError(null)
    setOpen(state)
  }

  const footer = (
    <Button
      size="medium"
      variant="primary"
      type="submit"
      form="cancel-order-form"
      className={Styles.submitButton}
      loading={isSubmitting}
      disabled={!choosenReason || isSubmitting}>
      {t('common.modal.confirm')}
    </Button>
  )

  const body = isFetching ? (
    <div className={Styles.loading}>
      <Spinner />
    </div>
  ) : (
    <form
      data-testid="cancel-order-form"
      onSubmit={handleSubmit(onSubmit)}
      id="cancel-order-form"
      className={Styles.form}>
      {!!submitError && (
        <Banner
          state="negative"
          type="boxed"
          description={submitError}
          icon={<InfoCircle />}
        />
      )}
      <Controller
        name="reasonType"
        control={control}
        render={({ field }) => (
          <RadioGroup onValueChange={field.onChange} defaultValue={field.value}>
            {data?.rejection_reasons?.map(reason => {
              const reasonString = getTranslation(reason)
              return (
                <RadioGroup.Row key={reasonString} value={reasonString}>
                  {reasonString}
                </RadioGroup.Row>
              )
            })}
            <RadioGroup.Row value={otherReason}>{otherReason}</RadioGroup.Row>
          </RadioGroup>
        )}
      />

      {choosenReason === otherReason && (
        <Controller
          name="comment"
          control={control}
          rules={{
            required: t('new-orders.cancel.comment.required') || ''
          }}
          render={({ field }) => (
            <div>
              <Textarea {...field} rows={4} />
              {errors.comment && <p>{errors.comment.message}</p>}
            </div>
          )}
        />
      )}
    </form>
  )

  return (
    <CommonModal className={Styles.modalBody}
      width={376}
      open={open}
      onOpenChange={handleOpenChange}
      title={t('orders.cancel-title')}
      footer={footer}
      body={body}>
      {children}
    </CommonModal>
  )
}

export default CancelOrderModal
